/**
 * ChallengeDetailOverlay — Challenge details + respond action + discussion
 * 
 * v2 — Added threaded discussion section:
 *   - Collapsible "Discussion" area with threaded comments
 *   - Only users with ≥1 project can comment
 *   - Challenge owner can delete any comment
 *   - Scrollable content area so long threads don't break layout
 * 
 * Shows the challenge prompt, origin project, response stats,
 * and skills needed. Amber accent line to distinguish from project overlays.
 * 
 * Place at: src/components/overlays/ChallengeDetailOverlay.jsx
 */

import React, { useRef, useEffect } from 'react';
import {
  X, Target, Calendar, ChevronRight, ExternalLink,
  Clock, Zap, MessageCircle, Edit3, Trash2
} from 'lucide-react';
import { cn, formatRelativeTime } from '../../utils/cn';
import { Button, Badge, Avatar } from '../ui';
import { useAuth } from '../../context/AuthContext';
import ChallengeComments from '../challenges/ChallengeComments';

export const ChallengeDetailOverlay = ({
  challenge,
  originProject,
  onClose,
  onRespond,
  onViewProject,
  onEdit,
  onDelete,
}) => {
  const { user } = useAuth();
  const overlayRef = useRef(null);

  const isOwner = user && (
    user.id === challenge.user_id ||
    user.id === challenge.userId ||
    user.id === challenge.requester_id ||
    user.id === challenge.requester?.id
  );

  const challengeOwnerId = challenge.requester_id || challenge.requesterId || challenge.user_id || challenge.userId;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (overlayRef.current && !overlayRef.current.contains(e.target)) onClose?.();
    };
    const handleEscape = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const title = challenge.challenge_title || challenge.challengeTitle || challenge.title || 'Challenge';
  const description = challenge.challenge_description || challenge.challengeDescription || challenge.description;
  const responseCount = challenge.response_count || challenge.responseCount || 0;
  const author = challenge.requester || challenge.author;
  const skills = challenge.skills_needed || challenge.skillsNeeded || [];
  const isOpen = challenge.status !== 'closed';
  const originImage = originProject?.image_url || originProject?.imageUrl;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 overlay-backdrop">
      <div
        ref={overlayRef}
        className="relative w-full max-w-lg max-h-[92vh] bg-zinc-900 border border-zinc-800/80
                   rounded-2xl overflow-hidden flex flex-col animate-overlay-in"
        style={{
          boxShadow: '0 25px 60px -12px rgba(0,0,0,0.5), 0 0 60px -20px rgba(245, 158, 11, 0.1)',
        }}
      >
        {/* Amber accent line */}
        <div className="h-[3px] flex-shrink-0" style={{ background: 'linear-gradient(90deg, #f59e0b60, #f59e0b15)' }} />

        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 bg-zinc-800/80 hover:bg-zinc-700 rounded-full transition-colors"
        >
          <X className="w-4 h-4 text-zinc-400" />
        </button>

        {/* ── Scrollable content ── */}
        <div
          className="flex-1 overflow-y-auto overscroll-contain"
          style={{ scrollbarWidth: 'thin', scrollbarColor: '#27272a transparent' }}
        >
          {/* Header */}
          <div className="px-5 sm:px-6 pt-5 pb-4">
            <div className="flex items-start gap-3.5">
              {/* Icon */}
              <div className="w-11 h-11 rounded-xl bg-amber-500/15 border border-amber-500/20 flex items-center justify-center flex-shrink-0">
                <Target className="w-5 h-5 text-amber-400" />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="px-2 py-0.5 rounded-md text-[11px] font-semibold bg-amber-500/15 text-amber-400 border border-amber-500/20">
                    Challenge
                  </span>
                  {isOpen ? (
                    <span className="px-2 py-0.5 rounded-md text-[11px] font-semibold bg-emerald-500/15 text-emerald-400">
                      Open
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-md text-[11px] font-semibold bg-zinc-700/50 text-zinc-500">
                      Closed
                    </span>
                  )}
                </div>

                <h2 className="text-lg font-bold text-white leading-snug">{title}</h2>

                {/* Author + time */}
                <div className="flex items-center gap-2 mt-1.5 text-[13px]">
                  <Avatar
                    src={author?.profile_picture_url || author?.profilePictureUrl}
                    alt={author?.username}
                    size="xs"
                  />
                  <span className="text-zinc-400">
                    <span className="text-zinc-200 font-medium">{author?.username || 'Unknown'}</span>
                  </span>
                  <span className="text-zinc-700">{'\u00b7'}</span>
                  <span className="text-zinc-500 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatRelativeTime(challenge.created_at || challenge.createdAt)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="px-5 sm:px-6 pb-5 space-y-4">
            {/* Challenge prompt */}
            {description && (
              <div className="p-3.5 rounded-xl bg-amber-500/[0.04] border border-amber-500/10">
                <p className="text-[13.5px] text-zinc-300 leading-relaxed whitespace-pre-wrap">
                  {description}
                </p>
              </div>
            )}

            {/* Skills needed */}
            {skills.length > 0 && (
              <div>
                <p className="text-[11px] text-zinc-600 uppercase tracking-wider font-semibold mb-1.5">
                  Skills needed
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {skills.map((skill, i) => (
                    <span
                      key={i}
                      className="px-2 py-0.5 rounded-md bg-zinc-800/50 text-[11px] text-amber-300/80 font-medium border border-amber-500/10"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Origin project */}
            {originProject && (
              <button
                onClick={() => onViewProject?.(originProject)}
                className="flex items-center gap-3 w-full p-2.5 rounded-xl bg-zinc-800/30 hover:bg-zinc-800/60
                           border border-zinc-800/40 hover:border-zinc-700/50 transition-all active:scale-[0.98] group text-left"
              >
                <div className="w-10 h-10 rounded-lg overflow-hidden bg-zinc-800 flex-shrink-0 border border-zinc-700/30">
                  {originImage ? (
                    <img src={originImage} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-zinc-600 text-sm font-bold">
                      {originProject.name?.[0]?.toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium text-zinc-200 truncate">{originProject.name}</p>
                  <p className="text-[11px] text-zinc-600 truncate">
                    {originProject.description?.slice(0, 60)}{originProject.description?.length > 60 ? '...' : ''}
                  </p>
                </div>
                <ChevronRight className="w-3.5 h-3.5 text-zinc-700 group-hover:text-zinc-500 flex-shrink-0" />
              </button>
            )}

            {/* Response count */}
            <div className="flex items-center gap-3 p-3.5 rounded-xl bg-zinc-800/20 border border-zinc-800/30">
              <div className="w-10 h-10 rounded-full bg-emerald-500/15 border border-emerald-500/20 flex items-center justify-center">
                <Zap className="w-4.5 h-4.5 text-emerald-400" />
              </div>
              <div>
                <p className="text-xl font-bold text-white tabular-nums">{responseCount}</p>
                <p className="text-[11px] text-zinc-500">response{responseCount !== 1 ? 's' : ''} so far</p>
              </div>
            </div>
          </div>

          {/* ── Discussion (threaded comments) ── */}
          <ChallengeComments
            challengeId={challenge.id}
            challengeOwnerId={challengeOwnerId}
          />
        </div>

        {/* ── Actions (pinned to bottom) ── */}
        <div className="px-5 sm:px-6 py-4 border-t border-zinc-800/50 bg-zinc-900/50 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <Button onClick={() => onRespond?.(challenge)} icon={Target} className="flex-1">
              Respond to Challenge
            </Button>
            {originProject && (
              <Button variant="secondary" onClick={() => onViewProject?.(originProject)} icon={ExternalLink}>
                Project
              </Button>
            )}
          </div>

          {isOwner && (
            <div className="flex items-center justify-center gap-3 mt-3 pt-3 border-t border-zinc-800/30">
              <button onClick={() => onEdit?.(challenge)} className="text-[12px] text-zinc-500 hover:text-zinc-300 transition-colors flex items-center gap-1">
                <Edit3 className="w-3 h-3" /> Edit
              </button>
              <button onClick={() => onDelete?.(challenge)} className="text-[12px] text-red-400/70 hover:text-red-400 transition-colors flex items-center gap-1">
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChallengeDetailOverlay;